// import * as Sentry from '@sentry/react';
import { createFileRoute, Link, redirect, useNavigate, useSearch } from '@tanstack/react-router';

import React from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2 } from 'lucide-react';
import { usePostUsersMutation, usePostUsersVerificationVerifyMutation } from '@/services';
import { toast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { z } from 'zod';
import { PasswordInput } from '@/components/ui/password-input';
import { useAuth } from '@/contexts/auth-context';

export const Route = createFileRoute('/_unauthenticated/signup')({
  component: SignUpPage,
  validateSearch: z.object({
    redirect: z.string().optional(),
  }),
});

function SignUpPage() {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const { redirect } = useSearch({ from: '/_unauthenticated/signup' });

  const [postUserMutation, { isLoading: isSigningUp }] = usePostUsersMutation();
  const [verifyEmail, { isLoading: isVerifying }] = usePostUsersVerificationVerifyMutation();

  // Check if this is an invitation flow
  const isInvitationFlow = redirect?.includes('/accept-invitation');

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { success } = await postUserMutation({
        body: {
          email,
          password,
          first_name: firstName,
          last_name: lastName,
          role: 'user',
        },
      }).unwrap();

      if (!success) {
        throw new Error('Failed to create account. Please try again.');
      }

      toast({
        title: 'Account created',
        description: 'Please check your email for a verification code.',
      });

      setShowVerification(true);
    } catch (error) {
      if (error instanceof Error) {
        toast({
          variant: 'error',
          title: 'Error',
          description: error.message,
        });
      } else {
        toast({
          variant: 'error',
          title: 'Error',
          description: 'An unexpected error occurred. Please try again.',
        });
      }
      console.error('Sign up error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmail = async () => {
    if (verificationCode.length !== 6) {
      toast({
        variant: 'error',
        title: 'Invalid code',
        description: 'Please enter all 6 digits of your verification code.',
      });
      return;
    }

    try {
      await verifyEmail({
        body: { code: verificationCode },
      }).unwrap();

      toast({
        title: 'Success',
        description: 'Your email has been verified!',
      });

      // Auto-login for invitation flows
      if (isInvitationFlow) {
        try {
          await authLogin(email, password);
          toast({
            title: 'Welcome!',
            description: 'You have been automatically logged in.',
          });
          // Use window.location.replace to redirect to the invitation acceptance page
          // This prevents the user from navigating back to the signup page
          window.location.replace(redirect!);
        } catch (loginError) {
          console.error('Auto-login failed:', loginError);
          toast({
            variant: 'error',
            title: 'Auto-login failed',
            description: 'Please log in manually to complete the invitation.',
          });
          // Fallback to login page with redirect
          navigate({ to: '/login', search: { redirect } });
        }
      } else {
        // Regular signup flow - redirect to login
        navigate({ to: '/login', search: { redirect } });
      }
    } catch (error: unknown) {
      console.error('Signup error:', error);
      // Sentry.captureException(error);
      toast({
        variant: 'error',
        title: 'Verification failed',
        description: 'The code is invalid or has expired. Please try again.',
      });
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-12 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-chart-8/20 via-chart-2/20 to-chart-1/20 dark:from-chart-8/40 dark:via-chart-2/40 dark:to-chart-1/40" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.08)_1px,transparent_0)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_at_center,black_60%,transparent_70%)]" />

      {/* Floating Elements */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-chart-8/10 rounded-full blur-xl animate-pulse" />
      <div className="absolute bottom-20 right-10 w-40 h-40 bg-chart-2/10 rounded-full blur-xl animate-pulse delay-700" />
      <div className="absolute top-1/2 right-1/3 w-24 h-24 bg-chart-1/10 rounded-full blur-xl animate-pulse delay-300" />

      <div className="relative w-full max-w-md space-y-8">
        {/* Card Container */}
        <div className="bg-card/80 backdrop-blur-xl border border-border/50 rounded-2xl p-8 shadow-2xl">
          <div className="space-y-2 text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-chart-8 to-chart-2 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <div className="w-4 h-4 bg-white rounded-sm" />
              </div>
            </div>
            <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
              Create an account
            </h1>
            <p className="text-lg text-muted-foreground">Join the IoT revolution today</p>
          </div>

          <form onSubmit={handleSignUp} className="space-y-6">
            <div className="space-y-4">
              {/* First and Last Name in the same row */}
              <div className="flex gap-4">
                <div className="flex-1 space-y-2">
                  <label htmlFor="firstName" className="block text-sm font-medium text-foreground">
                    First Name
                  </label>
                  <Input
                    id="firstName"
                    type="text"
                    placeholder="John"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    className="h-12 bg-background/50 border-border/70 focus:border-primary/50 focus:ring-primary/20 transition-all duration-200"
                  />
                </div>
                <div className="flex-1 space-y-2">
                  <label htmlFor="lastName" className="block text-sm font-medium text-foreground">
                    Last Name
                  </label>
                  <Input
                    id="lastName"
                    type="text"
                    placeholder="Doe"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    className="h-12 bg-background/50 border-border/70 focus:border-primary/50 focus:ring-primary/20 transition-all duration-200"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-medium text-foreground">
                  Email Address
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 bg-background/50 border-border/70 focus:border-primary/50 focus:ring-primary/20 transition-all duration-200"
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="password" className="block text-sm font-medium text-foreground">
                  Password
                </label>
                <PasswordInput
                  id="password"
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 bg-background/50 border-border/70 focus:border-primary/50 focus:ring-primary/20 transition-all duration-200"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading || isSigningUp}
              className="w-full h-12 text-base font-semibold bg-gradient-to-r from-chart-8 to-chart-2 hover:from-chart-8/90 hover:to-chart-2/90 shadow-lg hover:shadow-xl transition-all duration-200"
            >
              {loading || isSigningUp ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>

            <div className="text-center text-muted-foreground">
              Already have an account?{' '}
              <Link
                to="/login"
                className="text-primary hover:text-primary/80 font-medium transition-colors duration-200"
              >
                Sign in
              </Link>
            </div>
          </form>
        </div>
      </div>

      {/* Verification Dialog */}
      <Dialog open={showVerification} onOpenChange={setShowVerification} modal>
        <DialogContent
          className="sm:max-w-md bg-card/95 backdrop-blur-xl border-border/50"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <div className="mx-auto w-12 h-12 bg-gradient-to-br from-chart-2 to-chart-1 rounded-xl flex items-center justify-center mb-4">
              <div className="w-6 h-6 bg-white/20 rounded-lg flex items-center justify-center">
                <div className="w-3 h-3 bg-white rounded-sm" />
              </div>
            </div>
            <DialogTitle className="text-xl font-semibold text-center">Verify your email</DialogTitle>
            <DialogDescription className="text-center text-muted-foreground">
              We've sent a 6-digit verification code to <span className="font-medium text-foreground">{email}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col items-center space-y-6 py-4">
            <div className="my-4">
              <InputOTP
                value={verificationCode}
                onChange={setVerificationCode}
                maxLength={6}
                containerClassName="justify-center"
              >
                <InputOTPGroup>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <InputOTPSlot
                      key={i}
                      index={i}
                      className={cn(
                        'w-12 h-14 text-center text-xl font-semibold',
                        'bg-background/70 border-border/70',
                        'focus:ring-2 focus:ring-primary/20 focus:border-primary/50',
                        'transition-all duration-200',
                        'hover:border-primary/30',
                      )}
                    />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>

            <Button
              onClick={handleVerifyEmail}
              className="w-full h-12 bg-gradient-to-r from-chart-2 to-chart-1 hover:from-chart-2/90 hover:to-chart-1/90 font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
              disabled={isVerifying || verificationCode.length !== 6}
            >
              {isVerifying ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify Email'
              )}
            </Button>

            <p className="text-sm text-muted-foreground text-center">
              Didn't receive a code? Check your spam folder or{' '}
              <Button
                variant="link"
                className="p-0 h-auto text-primary hover:text-primary/80 font-medium"
                onClick={handleSignUp}
              >
                resend the code
              </Button>
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
